"use client";

import ErrorIcon from "public/next-public/common/icons/error-icon.svg";
import InfoIcon from "public/next-public/common/icons/info-icon.svg";
import ToastSuccessCheckIcon from "public/next-public/common/icons/toast-success-check-icon.svg";
import WarningIcon from "public/next-public/common/icons/warning-icon.svg";
import XIcon from "public/next-public/common/icons/x-close-icon.svg";
import React from "react";
import styles from "./alert.module.scss";

type AlertVariant = "neutral" | "info" | "success" | "warning" | "error";
type AlertSize = "xs" | "small" | "medium" | "large";
type AlertActionLocation = "right" | "below";

interface AlertAction {
  label: string;
  /** When provided, the CTA renders as a right-aligned underlined link (navigates) rather than a button. */
  href?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
}

interface AlertLink {
  label: string;
  href: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

interface AlertProps {
  variant?: AlertVariant;
  size?: AlertSize;
  /** Below the sm breakpoint (<576px), render at this size instead of `size`. */
  sizeDownSm?: "medium";
  title?: string;
  message?: React.ReactNode;
  links?: AlertLink[];
  action?: AlertAction;
  actionLocation?: AlertActionLocation;
  dismissible?: boolean;
  onDismiss?: () => void;
  role?: "alert" | "status";
  className?: string;
}

type IconComponent = React.FC<{ width?: number; height?: number; "aria-hidden"?: boolean }>;

const iconForVariant: Record<Exclude<AlertVariant, "neutral">, IconComponent> = {
  info: InfoIcon,
  success: ToastSuccessCheckIcon,
  warning: WarningIcon,
  error: ErrorIcon,
};

const statusIconSize: Record<AlertSize, number> = {
  xs: 16,
  small: 20,
  medium: 24,
  large: 24,
};

const closeIconSize: Record<AlertSize, number> = {
  xs: 16,
  small: 16,
  medium: 16,
  large: 20,
};

const Alert: React.FC<AlertProps> = ({
  variant = "info",
  size = "medium",
  sizeDownSm,
  title,
  message,
  links,
  action,
  actionLocation = "right",
  dismissible = false,
  onDismiss,
  role,
  className,
}) => {
  const resolvedRole = role ?? (variant === "error" || variant === "warning" ? "alert" : "status");
  const StatusIcon = variant === "neutral" ? null : iconForVariant[variant];
  const isInline = size === "xs";
  const hasAction = !!action;
  const hasDismiss = dismissible && !!onDismiss;
  // XS never stacks the CTA; it always sits inline regardless of `actionLocation`.
  const ctaBelow = hasAction && actionLocation === "below" && !isInline;

  const classes = [
    styles.Alert,
    styles[variant],
    styles[`size-${size}`],
    // XS holds its text on one line only to reserve width for an inline CTA.
    isInline && hasAction ? styles.hasInlineCta : "",
    sizeDownSm ? styles[`size-down-sm-${sizeDownSm}`] : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderCta = () => {
    if (!action) return null;
    if (action.href) {
      return (
        <a className={styles.ctaLink} href={action.href} onClick={action.onClick}>
          {action.label}
        </a>
      );
    }
    if (isInline) {
      return (
        <button type="button" className={styles.ctaLink} onClick={action.onClick}>
          {action.label}
        </button>
      );
    }
    return (
      <button type="button" className={styles.ctaButton} onClick={action.onClick}>
        {action.label}
      </button>
    );
  };

  return (
    <div className={classes} role={resolvedRole}>
      <span className={styles.iconStripe} aria-hidden>
        {StatusIcon && <StatusIcon width={statusIconSize[size]} height={statusIconSize[size]} aria-hidden />}
      </span>
      <div className={styles.body}>
        <div className={styles.row}>
          <div className={styles.bodyText}>
            {title && <p className={styles.title}>{title}</p>}
            {message && <p className={styles.message}>{message}</p>}
            {!isInline && !!links?.length && (
              <div className={styles.links}>
                {links.map((link, idx) => (
                  <a key={idx} className={styles.link} href={link.href} onClick={link.onClick}>
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          {hasAction && !ctaBelow && <div className={styles.ctaWrap}>{renderCta()}</div>}
          {hasDismiss && (
            <button type="button" className={styles.close} onClick={onDismiss} aria-label="Dismiss">
              <XIcon width={closeIconSize[size]} height={closeIconSize[size]} aria-hidden />
            </button>
          )}
        </div>
        {ctaBelow && <div className={styles.ctaBelow}>{renderCta()}</div>}
      </div>
    </div>
  );
};

export default Alert;
